<script setup lang="ts">
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { SubmissionJob } from "~/classes/models/submission-job/submission-job.model";

import { DocumentViewMode } from "~/types/enums/DocumentViewMode.enum";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
  job: {
    type: Object as PropType<SubmissionJob>,
    required: true,
  },
});

const db = useFirestore();

const submittedDocument = ref<SubmittedDocument | undefined>();
const revision = ref<Revision | undefined>();

onMounted(() => {
  const db = useFirestore();
  const submittedDocumentRef = doc(db, `/documents/${props.documentId}`);

  onSnapshot(submittedDocumentRef, (snapshot: any) => {
    submittedDocument.value = {
      ...snapshot.data(),
      id: snapshot.id,
    } as SubmittedDocument;
  });
});

watch(submittedDocument, (newSubmittedDocument, oldSubmittedDocument) => {
  if (newSubmittedDocument == undefined) return;

  if (
    newSubmittedDocument.state != oldSubmittedDocument?.state &&
    newSubmittedDocument.state == DocumentState.submitted
  ) {
    onSubmitted();
    return;
  }

  const revisionRef = collection(
    db,
    `/documents/${props.documentId}/revisions`
  );
  const revisionQuery = query(
    revisionRef,
    orderBy("lastUpdatedTimestamp", "desc")
  );
  onSnapshot(revisionQuery, (snapshot) => {
    if (snapshot.docs.length == 0) return;

    revision.value = {
      ...snapshot.docs[0].data(),
      id: snapshot.docs[0].id,
    } as Revision;
  });
});

const isUploading = computed(() => {
  if (submittedDocument.value == undefined) return false;

  return (
    [
      DocumentState.pending,
      DocumentState.uploading,
      DocumentState.converting,
      DocumentState.processing,
    ].includes(submittedDocument.value.state as DocumentState) == true
  );
});

const showSubmitted = ref(false);
const onSubmitted = async () => {
  await useDocuments().updateDocumentStateById(
    props.documentId,
    DocumentState.submitted
  );

  showSubmitted.value = true;
};
</script>

<template>
  <div v-auto-animate class="h-full">
    <div v-if="showSubmitted != true" class="h-full">
      <BaseLoading
        v-if="submittedDocument == undefined && revision == undefined"
      />
      <div v-if="submittedDocument != undefined" class="h-full">
        <div
          v-if="isUploading"
          class="flex flex-col items-center justify-center h-full"
        >
          <BaseIconLoading size="50" />
          <span class="mt-2 text-[22px]">
            Your submission is processing. Do not close this window.
          </span>
        </div>
        <div
          v-if="isUploading != true && revision"
          class="flex flex-col h-full min-h-0 overflow-auto"
        >
          <div class="grow min-h-0 overflow-auto">
            <!-- <pre>
            {{ submittedDocument }}
          </pre> -->
            <DocumentWrapper
              :document-id="documentId"
              :mode="DocumentViewMode.review"
              @on-submitted="
                () => {
                  onSubmitted();
                }
              "
            />
          </div>
          <!-- <div class="p-2 flex flex-col border-t">
            <BaseTextButton color="danger" @click="onDelete">
              Cancel Submission
            </BaseTextButton>
          </div> -->
        </div>
      </div>
    </div>
    <div v-if="showSubmitted == true" class="h-full">
      <div class="flex flex-col items-center justify-center h-full">
        <icon
          name="material-symbols:check-circle-outline"
          class="text-success-default"
          size="150"
        />
        <h2 class="text-h2">Success!</h2>
        <p class="text-h4 text-center mt-4">
          Your document has been submitted.
        </p>
        <BaseButton class="mt-4" @click="job.sheetController.close()">
          Done
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
